import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom } from 'jotai';
import { X } from 'lucide-react';

import { getProjectMedia } from '@/api/media';
import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import { env } from '@/constants/env';
import { getImages } from '@/features/estate-media/api/images';
import { SelectedImage } from '@/features/estate-media/components/selected-image';
import { createTempId } from '@/utils/tempId';
import { uniqBy } from '@/utils/uniqBy';

import type { ImageInAtom } from '../atoms/imageFiles';
import { selectedImageAtom, uploadedImagesAtom } from '../atoms/imageFiles';

export type MediaLibraryModalProps =
  | {
      type: 'estate';
      estateId: string;
    }
  | {
      type: 'project';
      projectId: string;
    };

interface MediaImage {
  estateId: string | null;
  id: string;
  name: string | null;
  createdAt: string;
  creatorId: string | null;
  mediaId?: string | null;
  fileName: string | null;
}

export const MediaLibraryModal = (props: MediaLibraryModalProps) => {
  const { t } = useTranslation();

  const [images, setImages] = useState<MediaImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useAtom(selectedImageAtom);
  const [, setUploadedImages] = useAtom(uploadedImagesAtom);

  const handleSelectImage = (image: MediaImage) => {
    // check if the image is already selected
    if (selectedImage?.originalImageId === image.mediaId) {
      setSelectedImage(undefined);
      return;
    }

    if (image.mediaId) {
      setSelectedImage({
        id: createTempId(),
        originalImage: `${env.API_URL}/images/${image.mediaId}`,
        originalImageId: image.mediaId,
        file: null,
        fileType: 'image/jpeg',
      } satisfies ImageInAtom);
    }
  };

  const handleSave = () => {
    if (selectedImage) {
      setSelectedImage(selectedImage);
      setUploadedImages((prev) => [...prev, selectedImage]);
    }
    closeModal();
  };

  const handleFetchImages = useCallback(async () => {
    try {
      setLoading(true);
      if (props.type === 'estate') {
        const res = await getImages(props.estateId);
        setImages(res.images);
      } else if (props.type === 'project') {
        const res = await getProjectMedia(props.projectId);
        setImages(uniqBy(res.images, 'id'));
      }
    } finally {
      setLoading(false);
    }
  }, [props]);

  useEffect(() => {
    handleFetchImages();
  }, [handleFetchImages]);

  return (
    <ModalBase className="flex max-h-[90vh] w-full max-w-[900px] flex-col overflow-hidden rounded-sm p-0">
      <div className="flex !h-[100px] items-center justify-between bg-text-primary pl-5 pr-6">
        <span className="text-sm font-semibold text-white">{t('mediaLibrary')}</span>
        <X className="cursor-pointer stroke-[4px] text-white" onClick={closeModal} />
      </div>
      <div className="flex h-full flex-1 flex-col overflow-y-auto p-4 pb-[75px]">
        {loading ? (
          <div className="flex flex-1 items-center justify-center bg-background">
            <Spinner className="size-12 [&_*]:stroke-primary-dark" />
          </div>
        ) : images.length ? (
          <>
            <div className="grid w-full grid-cols-6 gap-1 max-md:grid-cols-3 max-sm:grid-cols-2">
              {images.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className="cursor-pointer whitespace-normal break-words px-1"
                  onClick={() => handleSelectImage(file)}
                >
                  <SelectedImage
                    fileType={'fileType' in file ? (file.fileType as string) : ''}
                    fileName={file.fileName as string}
                    mediaId={file?.mediaId as string}
                    onClick={() => handleSelectImage(file)}
                    isSelected={selectedImage?.originalImageId === file.mediaId}
                  />
                </button>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 mt-auto flex h-[70px] w-full items-center justify-end bg-white pr-4">
              <Button disabled={!selectedImage} onClick={handleSave}>
                {t('save')}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <span>{t('noContent')}</span>
          </div>
        )}
      </div>
    </ModalBase>
  );
};
